import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { BackButton } from '@/components/ui/back-button'
import {
  Search,
  User,
  Truck,
  MapPin,
  Star,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  Phone,
  Mail,
  Filter
} from 'lucide-react'

interface DeliveryPartner {
  id: string
  applicant_name: string
  applicant_email: string
  applicant_phone: string
  vehicle_type: string
  delivery_zone: string
  status: string
  rating: number
  total_deliveries: number
  created_at: string
  availability: {
    monday: boolean
    tuesday: boolean
    wednesday: boolean
    thursday: boolean
    friday: boolean
    saturday: boolean
    sunday: boolean
  }
}

export function ManagementTeamPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [deliveryPartners, setDeliveryPartners] = useState<DeliveryPartner[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending' | 'blocked'>('all')
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [selectedPartner, setSelectedPartner] = useState<DeliveryPartner | null>(null)
  const [showPartnerDetails, setShowPartnerDetails] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    if (user.role !== 'manager') {
      navigate('/')
      return
    }

    fetchDeliveryPartners()
  }, [user, navigate, searchQuery, statusFilter])

  const fetchDeliveryPartners = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('delivery_partners')
        .select('*')
        .eq('manager_id', user?.id)
        .order('created_at', { ascending: false })

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }

      // Apply search filter
      if (searchQuery) {
        query = query.or(
          `applicant_name.ilike.%${searchQuery}%,applicant_email.ilike.%${searchQuery}%,delivery_zone.ilike.%${searchQuery}%`
        )
      }

      const { data, error } = await query
      if (error) throw error

      setDeliveryPartners(data || [])
    } catch (err) {
      console.error('Error fetching delivery partners:', err)
      setToastMessage('Erreur lors du chargement des livreurs')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Actif
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            En attente
          </span>
        )
      case 'blocked':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Bloqué
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        )
    }
  }

  const getVehicleTypeLabel = (type: string) => {
    switch (type) {
      case 'bike': return 'Vélo'
      case 'motorcycle': return 'Moto'
      case 'car': return 'Voiture'
      case 'van': return 'Camionnette'
      case 'none': return 'À pied'
      default: return type
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Partner Details Modal */}
      {showPartnerDetails && selectedPartner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">
                  Détails du Livreur
                </h3>
                <button
                  onClick={() => setShowPartnerDetails(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-6 space-y-6">
                {/* Personal Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Informations Personnelles</h4>
                  <div className="mt-2 bg-gray-50 p-4 rounded-lg">
                    <div className="flex flex-col sm:flex-row sm:items-center mb-4">
                      <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center mb-2 sm:mb-0">
                        <User className="h-6 w-6 text-gray-500" />
                      </div>
                      <div className="sm:ml-4">
                        <p className="text-sm font-medium text-gray-900">{selectedPartner.applicant_name}</p>
                        <div className="mt-1 flex items-center text-sm text-gray-500">
                          <Mail className="h-4 w-4 mr-1" />
                          {selectedPartner.applicant_email}
                        </div>
                        <div className="mt-1 flex items-center text-sm text-gray-500">
                          <Phone className="h-4 w-4 mr-1" />
                          {selectedPartner.applicant_phone}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-gray-500">Véhicule</p>
                        <p className="text-sm font-medium text-gray-900 capitalize">
                          {getVehicleTypeLabel(selectedPartner.vehicle_type)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Zone de livraison</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedPartner.delivery_zone}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Livraisons totales</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedPartner.total_deliveries || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Note</p>
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400" />
                          <p className="ml-1 text-sm font-medium text-gray-900">
                            {selectedPartner.rating?.toFixed(1) || '0.0'} / 5.0
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Availability */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Disponibilités</h4>
                  <div className="mt-2 grid grid-cols-3 sm:grid-cols-7 gap-2">
                    {selectedPartner.availability && Object.entries(selectedPartner.availability).map(([day, available]) => (
                      <div
                        key={day}
                        className={`p-3 rounded-lg text-center ${
                          available ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'
                        }`}
                      >
                        <p className="text-xs font-medium capitalize">{day.substring(0, 3)}</p>
                        <p className="mt-1 text-xs">
                          {available ? 'Disponible' : 'Indisponible'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-6 border-t flex justify-end">
                  <Button
                    variant="secondary"
                    onClick={() => setShowPartnerDetails(false)}
                  >
                    Fermer
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <BackButton to="/management" label="Retour" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Gestion de l'Équipe
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Gérez votre équipe de livreurs assignés
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un livreur..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                  className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="active">Actifs</option>
                  <option value="pending">En attente</option>
                  <option value="blocked">Bloqués</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : deliveryPartners.length === 0 ? (
              <div className="text-center py-12">
                <Truck className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun livreur</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Aucun livreur n'a été assigné à votre équipe pour le moment
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {deliveryPartners.map((partner) => (
                  <div 
                    key={partner.id}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      setSelectedPartner(partner)
                      setShowPartnerDetails(true)
                    }}
                  >
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-500" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900 line-clamp-1">{partner.applicant_name}</p>
                            <p className="text-xs text-gray-500 line-clamp-1">{partner.applicant_email}</p>
                          </div>
                        </div>
                        {getStatusBadge(partner.status)}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">Véhicule</p>
                          <div className="flex items-center mt-1">
                            <Truck className="h-4 w-4 text-gray-400 mr-1" />
                            <p className="text-sm text-gray-900 capitalize">
                              {getVehicleTypeLabel(partner.vehicle_type)}
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Zone</p>
                          <div className="flex items-center mt-1">
                            <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                            <p className="text-sm text-gray-900 truncate">
                              {partner.delivery_zone}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Livraisons</p>
                          <div className="flex items-center mt-1">
                            <Package className="h-4 w-4 text-gray-400 mr-1" />
                            <p className="text-sm text-gray-900">
                              {partner.total_deliveries || 0}
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Note</p>
                          <div className="flex items-center mt-1">
                            <Star className="h-4 w-4 text-yellow-400 mr-1" />
                            <p className="text-sm text-gray-900">
                              {partner.rating?.toFixed(1) || '0.0'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}